import React, { useMemo, useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Label } from 'recharts';

type Props = {
  name: string;
  profilePicture?: string | null;
  pending: number;
  inProgress: number;
  completed: number;
  cancelled: number;
};

const COLORS = {
  pending: '#FB923C', // orange-400
  inProgress: '#60A5FA', // blue-400 (light blue)
  completed: '#22C55E', // green-500 (darker green)
  cancelled: '#EF4444', // red-500
  empty: '#E5E7EB', // gray-200
};

const UserTaskDonut: React.FC<Props> = ({ name, profilePicture, pending, inProgress, completed, cancelled }) => {
  const [imageError, setImageError] = useState(false);
  
  // Get user initials
  const initials = useMemo(() => {
    return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase();
  }, [name]);
  
  // Reset error state when profile picture changes
  useEffect(() => {
    setImageError(false);
  }, [profilePicture]);
  // Convert strings to numbers if needed (backend returns strings)
  const pendingNum = typeof pending === 'string' ? parseInt(pending, 10) : pending;
  const inProgressNum = typeof inProgress === 'string' ? parseInt(inProgress, 10) : inProgress;
  const completedNum = typeof completed === 'string' ? parseInt(completed, 10) : completed;
  const cancelledNum = typeof cancelled === 'string' ? parseInt(cancelled, 10) : cancelled;
  
  const total = pendingNum + inProgressNum + completedNum + cancelledNum;

  const data = useMemo(() => {
    if (total === 0) {
      return [{ name: 'No tasks', value: 1, color: COLORS.empty, count: 0 }];
    }
    return [
      { name: 'Pending', value: pendingNum, color: COLORS.pending, count: pendingNum },
      { name: 'In Progress', value: inProgressNum, color: COLORS.inProgress, count: inProgressNum },
      { name: 'Completed', value: completedNum, color: COLORS.completed, count: completedNum },
      { name: 'Cancelled', value: cancelledNum, color: COLORS.cancelled, count: cancelledNum },
    ].filter(d => d.value > 0);
  }, [pendingNum, inProgressNum, completedNum, cancelledNum, total]);

  const renderLabel = (props: any) => {
    const { value } = props;
    if (total === 0 || value === 0) return '';
    return value; // Show only the task count
  };

  return (
    <div className="flex flex-col items-center">
      {/* Donut Chart with Avatar */}
      <div className="relative" style={{ width: '150px', height: '150px' }}>
        <PieChart width={150} height={150}>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx={75}
            cy={75}
            innerRadius={40}
            outerRadius={60}
            paddingAngle={total === 0 ? 0 : 2}
            stroke="none"
            label={renderLabel}
            labelLine={false}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
        </PieChart>
        {/* Avatar in center */}
        <div className="absolute" style={{ top: '53.5%', left: '53.5%', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}>
          <div className="w-16 h-16 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center overflow-hidden" style={{ minWidth: '4rem', minHeight: '4rem' }}>
            {profilePicture && !imageError ? (
              <img
                key={profilePicture}
                src={`${profilePicture}${profilePicture.includes('?') ? '&' : '?'}v=1`}
                alt={name}
                className="w-full h-full object-cover object-center"
                style={{ aspectRatio: '1 / 1' }}
                onError={() => setImageError(true)}
              />
            ) : (
              <span className="text-gray-600 font-semibold text-base">{initials}</span>
            )}
          </div>
        </div>
      </div>
      
      {/* User Name */}
      <p className="mt-2 text-sm font-medium text-gray-900 text-center">{name}</p>
    </div>
  );
};

export default UserTaskDonut;

